import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, Heart, Users, Globe, Smile, Apple, GraduationCap, MessageCircle,
  Send, CheckCircle2, Loader2, Star, Phone, Mail, MapPin, Instagram, Facebook,
  ChevronDown, ChevronUp, Calendar as CalIcon, Baby, Sparkles, Flower2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Curated, royalty-free daycare stock photography (Unsplash CDN).
// Each photo ID is verified from Unsplash's "daycare" search and confirmed children/childcare-themed.
// Each tenant deterministically gets one fallback based on slug hash so different daycares
// look different out of the box. Owners can override via Website Builder → Hero upload.
const STOCK_HERO_PHOTOS = [
  // "A group of children playing with toys on the floor"
  'https://images.unsplash.com/photo-1578349035260-9f3d4042f1f7?auto=format&fit=crop&w=1400&q=80',
  // "Child building with four boxes" (kid stacking blocks)
  'https://images.unsplash.com/photo-1484820540004-14229fe36ca4?auto=format&fit=crop&w=1400&q=80',
  // "Boy in white shirt writing on white paper"
  'https://images.unsplash.com/photo-1587323655395-b1c77a12c89a?auto=format&fit=crop&w=1400&q=80',
  // "Boy playing cube on white wooden table"
  'https://images.unsplash.com/photo-1501686637-b7aa9c48a882?auto=format&fit=crop&w=1400&q=80',
  // "A child is drawing on a piece of paper"
  'https://images.unsplash.com/photo-1560130958-0ea787c275de?auto=format&fit=crop&w=1400&q=80',
  // "Girl in pink shirt writing on white paper"
  'https://images.unsplash.com/photo-1586694680938-9682c9e1f736?auto=format&fit=crop&w=1400&q=80',
  // "Toddler playing with toy"
  'https://images.unsplash.com/photo-1557452765-5b57d100c1dc?auto=format&fit=crop&w=1400&q=80',
  // "Girl in blue denim dungarees" (toddler)
  'https://images.unsplash.com/photo-1600880291319-1a7499c191e8?auto=format&fit=crop&w=1400&q=80',
];

function pickStockPhoto(slug = '') {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) hash = (hash * 31 + slug.charCodeAt(i)) | 0;
  const idx = Math.abs(hash) % STOCK_HERO_PHOTOS.length;
  return STOCK_HERO_PHOTOS[idx];
}

const ICON_MAP = {
  'shield-check': ShieldCheck, 'heart': Heart, 'users': Users, 'globe': Globe,
  'smile': Smile, 'apple': Apple, 'graduation-cap': GraduationCap,
  'message-circle': MessageCircle, 'baby': Baby,
};

const PROGRAMS = ['Infant Care', 'Toddler Care', 'Preschool', 'Pre-K', 'After School'];

// Translation helper: pick the *_es field when language is es, else *_en or plain key.
function t(obj, baseKey, language) {
  if (!obj) return '';
  return obj[`${baseKey}_${language}`] || obj[`${baseKey}_en`] || obj[baseKey] || '';
}

export default function DaycarePublicHome() {
  const { tenant_slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sourceParam = searchParams.get('source') || 'website';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('es');
  const [openFaq, setOpenFaq] = useState(null);
  const enrollRef = useRef(null);
  const tourRef = useRef(null);
  const contactRef = useRef(null);

  useEffect(() => {
    if (!tenant_slug) return;
    axios.get(`${API}/public/daycare-website/${tenant_slug}`)
      .then(res => {
        setData(res.data);
        setLanguage(res.data?.content?.language || 'es');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tenant_slug]);

  if (loading) return <div style={fullPage}><Loader2 size={32} className="animate-spin" /></div>;
  if (!data) return <div style={fullPage}><p>Daycare not found.</p></div>;

  const { branding, content, programs, name } = data;
  const primary = branding.primary_color || '#22C55E';
  const secondary = branding.secondary_color || '#FACC15';
  const accent = branding.accent_color || '#4F46E5';
  const brandName = branding.brand_name || name;
  const logo = branding.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}` : '';

  const scrollTo = (ref) => ref?.current?.scrollIntoView({ behavior: 'smooth' });

  const tr = {
    nav_programs: language === 'es' ? 'Programas' : 'Programs',
    nav_about: language === 'es' ? 'Nosotros' : 'About',
    nav_gallery: language === 'es' ? 'Galería' : 'Gallery',
    nav_contact: language === 'es' ? 'Contacto' : 'Contact',
    enroll_now: language === 'es' ? 'Inscríbete Ahora' : 'Enroll Now',
    schedule_tour: language === 'es' ? 'Agendar Tour' : 'Schedule Tour',
    why_choose: language === 'es' ? '¿Por qué elegirnos?' : 'Why Choose Us?',
    why_sub: language === 'es' ? 'Lo que hace de nuestra guardería un lugar especial.' : "What makes our daycare a special place.",
    programs_title: language === 'es' ? 'Nuestros Programas' : 'Our Programs',
    programs_sub: language === 'es' ? 'Diseñados para cada etapa del desarrollo de su hijo.' : "Designed for every stage of your child's growth.",
    age: language === 'es' ? 'Edad' : 'Age',
    learn_more: language === 'es' ? 'Conocer Más' : 'Learn More',
    testimonials_title: language === 'es' ? 'Lo que dicen las familias' : 'What Families Say',
    faq_title: language === 'es' ? 'Preguntas Frecuentes' : 'Frequently Asked Questions',
    contact_title: language === 'es' ? 'Visítanos o Contáctanos' : 'Visit or Contact Us',
    enrollment_title: language === 'es' ? '¡Estamos inscribiendo!' : "Now Enrolling!",
    enrollment_sub: language === 'es' ? 'Complete el formulario y nos pondremos en contacto pronto.' : "Fill out the form and we'll get back to you soon.",
    tour_title: language === 'es' ? 'Agenda una visita' : 'Book a Tour',
    tour_sub: language === 'es' ? 'Ven a conocer nuestras instalaciones.' : 'Come meet our team and see our space.',
    footer_rights: language === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.',
    admin_login: language === 'es' ? 'Acceso administrador' : 'Admin login',
    limited_spots: language === 'es' ? '⚡ Cupos limitados — ¡Inscríbete hoy!' : '⚡ Limited Spots — Enroll Today!',
    cta_band_title: language === 'es' ? '¿Listo para darle a su hijo el mejor comienzo?' : 'Ready to give your child the best start?',
    cta_band_sub: language === 'es' ? 'Plazas limitadas para el próximo trimestre. ¡Reserva tu visita hoy!' : 'Limited spots for next term. Book your tour today!',
    request_info: language === 'es' ? 'Solicitar Info' : 'Request Info',
  };

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }} data-testid="daycare-public-home">
      {/* ======= TOP ANNOUNCEMENT BAR ======= */}
      <div style={{
        background: `linear-gradient(90deg, ${primary}, ${primary}dd)`,
        color: '#fff', padding: '8px 16px', textAlign: 'center', fontSize: 13, fontWeight: 600,
      }} data-testid="announcement-bar">
        {tr.limited_spots} &nbsp;·&nbsp;
        <button
          onClick={() => scrollTo(enrollRef)}
          style={{ background: 'transparent', border: 0, color: '#fff', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
        >
          {language === 'es' ? 'Reserva ahora →' : 'Reserve now →'}
        </button>
      </div>

      {/* ======= NAVBAR ======= */}
      <header style={navStyle}>
        <div style={navInner}>
          <a href="#top" style={brandWrap}>
            {logo ? (
              <img
                src={logo}
                alt={brandName}
                style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'grid'; }}
              />
            ) : null}
            <div
              style={{
                display: logo ? 'none' : 'grid',
                width: 56, height: 56, borderRadius: 12,
                background: `linear-gradient(135deg, ${primary}, ${secondary})`,
                color: '#fff', placeItems: 'center',
                boxShadow: `0 4px 12px ${primary}33`,
              }}
            >
              <Flower2 size={28} />
            </div>
            <span style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{brandName}</span>
          </a>
          <nav style={navLinks} className="lsg-nav-links">
            <a href="#programs" style={navA}>{tr.nav_programs}</a>
            <a href="#about" style={navA}>{tr.nav_about}</a>
            <a href="#gallery" style={navA}>{tr.nav_gallery}</a>
            <a href="#contact" style={navA}>{tr.nav_contact}</a>
          </nav>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={langToggle}>
              <button onClick={() => setLanguage('en')} data-testid="lang-en" style={{ ...langBtn, background: language === 'en' ? primary : 'transparent', color: language === 'en' ? '#fff' : '#475569' }}>EN</button>
              <button onClick={() => setLanguage('es')} data-testid="lang-es" style={{ ...langBtn, background: language === 'es' ? primary : 'transparent', color: language === 'es' ? '#fff' : '#475569' }}>ES</button>
            </div>
            <button onClick={() => scrollTo(enrollRef)} data-testid="nav-enroll-btn" style={{ ...primaryBtn(primary), padding: '8px 16px', fontSize: 13 }}>
              {tr.enroll_now}
            </button>
          </div>
        </div>
      </header>

      {/* ======= HERO ======= */}
      <section style={{ background: `linear-gradient(135deg, ${primary}11 0%, ${secondary}22 100%)`, padding: '60px 20px' }} id="top">
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'center' }} className="lsg-hero-grid">
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', borderRadius: 999, padding: '6px 14px', boxShadow: '0 2px 8px rgba(15,23,42,.06)', marginBottom: 20 }}>
              <span style={{ background: secondary, width: 8, height: 8, borderRadius: '50%' }} />
              <span style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>{language === 'es' ? '✨ Inscripciones abiertas' : '✨ Now Enrolling'}</span>
            </div>
            <h1 style={{ fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 800, color: '#0f172a', lineHeight: 1.1, margin: '0 0 20px' }}>
              {t(content.hero, 'headline', language).split(' ').map((word, i, arr) => (
                <span key={i} style={i >= arr.length - 2 ? { color: primary } : {}}>{word} </span>
              ))}
            </h1>
            <p style={{ fontSize: 18, color: '#475569', lineHeight: 1.6, marginBottom: 28 }}>
              {t(content.hero, 'subheadline', language)}
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 32 }}>
              <button onClick={() => scrollTo(enrollRef)} style={primaryBtn(primary)} data-testid="hero-enroll-btn">
                {t(content.hero, 'cta_primary', language)}
              </button>
              <button onClick={() => scrollTo(tourRef)} style={secondaryBtn(primary)} data-testid="hero-tour-btn">
                <CalIcon size={16} /> {t(content.hero, 'cta_secondary', language)}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {content.hero.badges.map((badge, i) => {
                const Icon = ICON_MAP[badge.icon] || ShieldCheck;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: i === 0 ? primary : i === 1 ? secondary : accent, display: 'grid', placeItems: 'center' }}>
                      <Icon size={18} color="#fff" />
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{t(badge, 'label', language)}</div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Hero image — owner upload, else curated daycare stock photo */}
          <div style={{ position: 'relative' }}>
            <HeroImage
              uploadedUrl={content.hero.hero_image_url}
              slug={tenant_slug}
              brandName={brandName}
              primary={primary}
              secondary={secondary}
            />
          </div>
        </div>
      </section>

      {/* ======= STATS ======= */}
      <section style={{ padding: '40px 20px', background: '#fff' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 20 }}>
          {content.stats.map((s, i) => (
            <div key={i} style={{ textAlign: 'center', padding: 20 }}>
              <div style={{ fontSize: 40, fontWeight: 800, color: primary }}>{s.value}</div>
              <div style={{ fontSize: 14, color: '#64748b', marginTop: 4 }}>{t(s, 'label', language)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ======= WHY CHOOSE US ======= */}
      <section id="about" style={{ padding: '64px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <SectionHeader title={tr.why_choose} sub={tr.why_sub} primary={primary} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginTop: 32 }}>
            {content.why_choose_us.map((item, i) => {
              const Icon = ICON_MAP[item.icon] || ShieldCheck;
              return (
                <div key={i} style={card}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: `${primary}15`, color: primary, display: 'grid', placeItems: 'center', marginBottom: 14 }}>
                    <Icon size={22} />
                  </div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 6px' }}>{t(item, 'title', language)}</h3>
                  <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5, margin: 0 }}>{t(item, 'desc', language)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ======= PROGRAMS ======= */}
      <section id="programs" style={{ padding: '64px 20px', background: '#fff' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <SectionHeader title={tr.programs_title} sub={tr.programs_sub} primary={primary} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, marginTop: 32 }}>
            {programs.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#94a3b8' }}>
                {language === 'es' ? 'Próximamente.' : 'Coming soon.'}
              </div>
            ) : programs.map((p, idx) => {
              const programImg = p.image_url
                ? (p.image_url.startsWith('http') ? p.image_url : `${process.env.REACT_APP_BACKEND_URL}${p.image_url}`)
                : STOCK_HERO_PHOTOS[(idx + 1) % STOCK_HERO_PHOTOS.length];
              return (
              <div key={p.id} style={{ ...card, padding: 0, overflow: 'hidden' }}>
                <div style={{ aspectRatio: '16/10', overflow: 'hidden', background: `linear-gradient(135deg, ${primary}22, ${secondary}22)` }}>
                  <img src={programImg} alt={p.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div style={{ padding: 18 }}>
                  <h3 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>{language === 'es' && p.name_es ? p.name_es : p.name}</h3>
                  {p.age_range && <div style={{ fontSize: 12, color: primary, fontWeight: 600, marginBottom: 8 }}>{tr.age}: {p.age_range}</div>}
                  <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5, margin: '0 0 12px' }}>{language === 'es' && p.description_es ? p.description_es : p.description}</p>
                  <button onClick={() => scrollTo(enrollRef)} style={{ background: 'transparent', border: `1px solid ${primary}`, color: primary, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {tr.learn_more} →
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ======= CTA BAND — sandwiched between Programs and Testimonials ======= */}
      <section style={{ padding: '40px 20px', background: `linear-gradient(135deg, ${primary}, ${primary}cc)` }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 20, color: '#fff' }}>
          <div>
            <h3 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 4px' }}>{tr.cta_band_title}</h3>
            <p style={{ fontSize: 14, opacity: 0.95, margin: 0 }}>{tr.cta_band_sub}</p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={() => scrollTo(tourRef)} style={{ background: '#fff', color: primary, border: 0, padding: '12px 22px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }} data-testid="cta-band-tour">
              <CalIcon size={16} /> {tr.schedule_tour}
            </button>
            <button onClick={() => scrollTo(enrollRef)} style={{ background: 'rgba(255,255,255,.2)', color: '#fff', border: '2px solid rgba(255,255,255,.5)', padding: '12px 22px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' }} data-testid="cta-band-enroll">
              {tr.enroll_now}
            </button>
          </div>
        </div>
      </section>

      {/* ======= TESTIMONIALS ======= */}
      <section style={{ padding: '64px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <SectionHeader title={tr.testimonials_title} primary={primary} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginTop: 32 }}>
            {content.testimonials.map((tst, i) => (
              <div key={i} style={card}>
                <div style={{ display: 'flex', gap: 2, marginBottom: 10 }}>
                  {[...Array(tst.rating || 5)].map((_, j) => <Star key={j} size={14} color={secondary} fill={secondary} />)}
                </div>
                <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.6, marginBottom: 12, fontStyle: 'italic' }}>"{t(tst, 'review', language)}"</p>
                <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 700 }}>— {tst.name}</div>
              </div>
            ))}
          </div>
          {/* Inline CTA after social proof */}
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <button onClick={() => scrollTo(enrollRef)} style={{ ...primaryBtn(primary), padding: '14px 28px', fontSize: 15 }} data-testid="cta-after-testimonials">
              <Sparkles size={16} /> {language === 'es' ? 'Únete a estas familias' : 'Join these families'}
            </button>
          </div>
        </div>
      </section>

      {/* ======= GALLERY ======= */}
      <section id="gallery" style={{ padding: '64px 20px', background: '#fff' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <SectionHeader title={language === 'es' ? 'Nuestro Espacio' : 'Our Space'} primary={primary} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 32 }}>
            {(content.gallery && content.gallery.length > 0
              ? content.gallery
              : STOCK_HERO_PHOTOS.map(url => ({ image_url: url }))
            ).map((g, i) => (
              <div key={i} style={{
                aspectRatio: '1', borderRadius: 16, overflow: 'hidden',
                background: `linear-gradient(135deg, ${primary}22, ${secondary}22)`,
              }}>
                {g.image_url && (
                  <img
                    src={g.image_url.startsWith('http') ? g.image_url : `${process.env.REACT_APP_BACKEND_URL}${g.image_url}`}
                    alt=""
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= FAQ ======= */}
      <section style={{ padding: '64px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <SectionHeader title={tr.faq_title} primary={primary} />
          <div style={{ marginTop: 32 }}>
            {content.faq.map((item, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 12, padding: '0', marginBottom: 10, border: '1px solid #e2e8f0' }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{ width: '100%', textAlign: 'left', padding: '16px 20px', background: 'transparent', border: 0, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{t(item, 'q', language)}</span>
                  {openFaq === i ? <ChevronUp size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
                </button>
                {openFaq === i && (
                  <div style={{ padding: '0 20px 16px', fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{t(item, 'a', language)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= TOUR BOOKING ======= */}
      <section ref={tourRef} style={{ padding: '64px 20px', background: '#fff' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <SectionHeader title={tr.tour_title} sub={tr.tour_sub} primary={primary} />
          <TourForm tenant_slug={tenant_slug} primary={primary} language={language} />
        </div>
      </section>

      {/* ======= ENROLLMENT FORM ======= */}
      <section ref={enrollRef} id="enroll" style={{ padding: '64px 20px', background: `linear-gradient(135deg, ${primary}, ${primary}cc)`, color: '#fff' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 480px', gap: 48, alignItems: 'start' }} className="lsg-enroll-grid">
          <div>
            <h2 style={{ fontSize: 36, fontWeight: 800, margin: '0 0 16px' }}>{tr.enrollment_title} ☀️</h2>
            <p style={{ fontSize: 16, lineHeight: 1.6, opacity: 0.95 }}>{tr.enrollment_sub}</p>
            <div style={{ marginTop: 24, display: 'grid', gap: 12 }}>
              {content.contact.phone && <ContactRow icon={<Phone size={18} />} text={content.contact.phone} />}
              {content.contact.email && <ContactRow icon={<Mail size={18} />} text={content.contact.email} />}
              {content.contact.address && <ContactRow icon={<MapPin size={18} />} text={content.contact.address} />}
            </div>
          </div>
          <EmbeddedEnrollForm tenant_slug={tenant_slug} primary={primary} language={language} source={sourceParam} programs={programs} />
        </div>
      </section>

      {/* ======= CONTACT ======= */}
      <section ref={contactRef} id="contact" style={{ padding: '64px 20px', background: '#0f172a', color: '#fff' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          {/* Google Map embed when address is set */}
          {content.contact.address && (
            <div style={{ marginBottom: 32, borderRadius: 16, overflow: 'hidden', height: 280, border: '1px solid #1e293b' }}>
              <iframe
                title="Google Map"
                src={`https://www.google.com/maps?q=${encodeURIComponent(content.contact.address)}&output=embed`}
                style={{ width: '100%', height: '100%', border: 0, filter: 'grayscale(0.2)' }}
                loading="lazy"
                data-testid="contact-map"
              />
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 32 }}>
          <div>
            <h3 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 12px' }}>{brandName}</h3>
            <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.6 }}>
              {language === 'es' ? 'Cuidado bilingüe de calidad con un currículo basado en el juego.' : 'Quality bilingual care with a play-based curriculum.'}
            </p>
          </div>
          <div>
            <h4 style={footerH}>{tr.nav_contact}</h4>
            {content.contact.phone && <FooterRow icon={<Phone size={14} />} text={content.contact.phone} />}
            {content.contact.email && <FooterRow icon={<Mail size={14} />} text={content.contact.email} />}
            {content.contact.address && <FooterRow icon={<MapPin size={14} />} text={content.contact.address} />}
          </div>
          <div>
            <h4 style={footerH}>{language === 'es' ? 'Síguenos' : 'Follow Us'}</h4>
            <div style={{ display: 'flex', gap: 12 }}>
              {content.contact.facebook_url && <a href={content.contact.facebook_url} target="_blank" rel="noreferrer" style={footerLink}><Facebook size={18} /></a>}
              {content.contact.instagram_url && <a href={content.contact.instagram_url} target="_blank" rel="noreferrer" style={footerLink}><Instagram size={18} /></a>}
              {!content.contact.facebook_url && !content.contact.instagram_url && (
                <span style={{ color: '#64748b', fontSize: 12 }}>{language === 'es' ? 'Próximamente' : 'Coming soon'}</span>
              )}
            </div>
          </div>
          <div>
            <h4 style={footerH}>{language === 'es' ? 'Enlaces' : 'Links'}</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <a href="#programs" style={footerLink}>{tr.nav_programs}</a>
              <a href="#enroll" style={footerLink}>{tr.enroll_now}</a>
              <button onClick={() => navigate(`/${tenant_slug}/login`)} style={{ ...footerLink, background: 'transparent', border: 0, padding: 0, textAlign: 'left', cursor: 'pointer' }} data-testid="footer-admin-login">
                {tr.admin_login} →
              </button>
            </div>
          </div>
        </div>
        </div>
        <div style={{ maxWidth: 1180, margin: '32px auto 0', paddingTop: 20, borderTop: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>© {new Date().getFullYear()} {brandName}. {tr.footer_rights}</p>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Powered by DepinaCRM</p>
        </div>
      </section>

      {/* ======= STICKY FLOATING CTAs ======= */}
      {/* Desktop: vertical pill on right edge */}
      <button
        onClick={() => scrollTo(enrollRef)}
        data-testid="floating-cta-desktop"
        className="lsg-floating-desktop"
        aria-label={tr.request_info}
        style={{
          position: 'fixed', right: 0, top: '40%',
          transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: '100% 0%',
          background: primary, color: '#fff', border: 0,
          padding: '12px 22px', borderTopLeftRadius: 12, borderTopRightRadius: 12,
          fontSize: 13, fontWeight: 700, cursor: 'pointer',
          boxShadow: '0 8px 24px rgba(15,23,42,.18)',
          display: 'inline-flex', alignItems: 'center', gap: 8, zIndex: 90,
        }}
      >
        <Send size={14} /> {tr.request_info}
      </button>

      {/* Mobile: full-width bottom action bar */}
      <div className="lsg-floating-mobile" style={{
        position: 'fixed', left: 0, right: 0, bottom: 0,
        background: '#fff', padding: 12, zIndex: 90,
        boxShadow: '0 -4px 20px rgba(15,23,42,.12)',
        display: 'none', gap: 8,
      }} data-testid="floating-cta-mobile">
        <button onClick={() => scrollTo(tourRef)} style={{ flex: 1, background: '#fff', color: primary, border: `2px solid ${primary}`, padding: '12px', borderRadius: 10, fontSize: 14, fontWeight: 700 }}>
          <CalIcon size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          {tr.schedule_tour}
        </button>
        <button onClick={() => scrollTo(enrollRef)} style={{ flex: 1, background: primary, color: '#fff', border: 0, padding: '12px', borderRadius: 10, fontSize: 14, fontWeight: 700 }}>
          {tr.enroll_now}
        </button>
      </div>

      <style>{`
        @media (max-width: 920px) {
          .lsg-hero-grid, .lsg-enroll-grid { grid-template-columns: 1fr !important; }
          .lsg-nav-links { display: none !important; }
          .lsg-floating-desktop { display: none !important; }
          .lsg-floating-mobile { display: flex !important; }
          body { padding-bottom: 80px !important; }
        }
      `}</style>
    </div>
  );
}

// ============ Hero Image (reusable across daycare websites) ============
function HeroImage({ uploadedUrl, slug, brandName, primary, secondary }) {
  // Owner-uploaded image (relative URL) gets prefixed with backend host; else use curated stock.
  const resolvedUrl = uploadedUrl
    ? (uploadedUrl.startsWith('http') ? uploadedUrl : `${process.env.REACT_APP_BACKEND_URL}${uploadedUrl}`)
    : pickStockPhoto(slug);

  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  return (
    <div
      data-testid="hero-image"
      style={{
        position: 'relative',
        aspectRatio: '4/3',
        borderRadius: 24,
        overflow: 'hidden',
        boxShadow: '0 30px 60px rgba(15,23,42,.18)',
        background: `linear-gradient(135deg, ${primary}22, ${secondary}22)`,
      }}
    >
      {/* The real photo */}
      {!errored && (
        <img
          src={resolvedUrl}
          alt={brandName ? `${brandName} — children learning and playing` : 'Children learning and playing'}
          loading="eager"
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', transition: 'opacity .35s ease',
            opacity: loaded ? 1 : 0,
          }}
        />
      )}
      {/* Soft brand overlay for readability + warmth */}
      {!errored && (
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(135deg, transparent 40%, ${primary}22 100%)`,
          pointerEvents: 'none',
        }} />
      )}
      {/* Loading skeleton */}
      {!loaded && !errored && (
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(135deg, ${primary}33, ${secondary}33)`,
          display: 'grid', placeItems: 'center', color: '#fff', fontSize: 14,
        }}>
          Loading photo…
        </div>
      )}
      {/* Last-resort fallback if even the stock photo fails */}
      {errored && (
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(135deg, ${primary}, ${secondary})`,
          display: 'grid', placeItems: 'center', color: '#fff', fontSize: 56, fontWeight: 800,
        }}>
          {(brandName || 'D').charAt(0).toUpperCase()}
        </div>
      )}
      {/* Floating trust pill — "Real Photos" */}
      <div style={{
        position: 'absolute', left: 16, bottom: 16,
        background: 'rgba(255,255,255,.95)',
        color: '#0f172a', padding: '8px 14px', borderRadius: 999,
        fontSize: 12, fontWeight: 700,
        boxShadow: '0 8px 20px rgba(15,23,42,.15)',
        backdropFilter: 'blur(8px)',
        display: 'inline-flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ background: '#22c55e', width: 8, height: 8, borderRadius: '50%' }} />
        Open for tours this week
      </div>
    </div>
  );
}

// ============ Reusable bits ============

function SectionHeader({ title, sub, primary }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: '#0f172a', margin: '0 0 8px' }}>
        {title}
      </h2>
      {sub && <p style={{ fontSize: 15, color: '#64748b', margin: 0 }}>{sub}</p>}
      <div style={{ width: 60, height: 4, background: primary, borderRadius: 2, margin: '20px auto 0' }} />
    </div>
  );
}

function ContactRow({ icon, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14 }}>
      <div style={{ opacity: 0.9 }}>{icon}</div>
      <span>{text}</span>
    </div>
  );
}

function FooterRow({ icon, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 13, color: '#94a3b8' }}>
      {icon}<span>{text}</span>
    </div>
  );
}

function EmbeddedEnrollForm({ tenant_slug, primary, language, source, programs }) {
  const [form, setForm] = useState({
    parent_name: '', parent_email: '', parent_phone: '',
    child_name: '', child_age: '', program_interest: '',
    desired_start_date: '', message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const update = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const lbl = {
    name: language === 'es' ? 'Nombre del Padre/Madre *' : 'Parent Name *',
    phone: language === 'es' ? 'Teléfono' : 'Phone',
    email: language === 'es' ? 'Correo Electrónico' : 'Email',
    child: language === 'es' ? 'Nombre del Niño' : "Child's Name",
    age: language === 'es' ? 'Edad del Niño' : "Child's Age",
    prog: language === 'es' ? 'Programa de Interés' : 'Program of Interest',
    start: language === 'es' ? 'Fecha de Inicio Deseada' : 'Desired Start Date',
    msg: language === 'es' ? 'Mensaje (Opcional)' : 'Message (Optional)',
    submit: language === 'es' ? 'Solicitar Información' : 'Request Information',
    thanks: language === 'es' ? '¡Gracias!' : 'Thank you!',
    thanks_sub: language === 'es' ? 'Recibimos su solicitud y nos pondremos en contacto pronto.' : 'We received your request and will contact you soon.',
    select: language === 'es' ? 'Seleccionar' : 'Select',
    another: language === 'es' ? 'Enviar otra solicitud' : 'Submit another inquiry',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.parent_name) { toast.error(language === 'es' ? 'Su nombre es obligatorio' : 'Your name is required'); return; }
    if (!form.parent_email && !form.parent_phone) { toast.error(language === 'es' ? 'Comparta un correo o teléfono' : 'Share an email or phone'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/daycare-lead`, { tenant_slug, source, ...form });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div style={{ background: '#fff', color: '#0f172a', borderRadius: 18, padding: 28, textAlign: 'center', boxShadow: '0 20px 60px rgba(15,23,42,.15)' }} data-testid="enroll-success-home">
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#dcfce7', display: 'grid', placeItems: 'center', margin: '0 auto 16px' }}>
          <CheckCircle2 size={32} color="#16a34a" />
        </div>
        <h3 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>{lbl.thanks}</h3>
        <p style={{ color: '#64748b', margin: 0 }}>{lbl.thanks_sub} 🌻</p>
        <button onClick={() => { setSubmitted(false); setForm({ parent_name: '', parent_email: '', parent_phone: '', child_name: '', child_age: '', program_interest: '', desired_start_date: '', message: '' }); }} style={{ marginTop: 16, padding: '10px 16px', background: primary, color: '#fff', border: 0, borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          {lbl.another}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ background: '#fff', color: '#0f172a', borderRadius: 18, padding: 28, boxShadow: '0 20px 60px rgba(15,23,42,.15)' }} data-testid="enroll-form-home">
      <F label={lbl.name}><input required value={form.parent_name} onChange={update('parent_name')} data-testid="home-enroll-name" /></F>
      <F label={lbl.phone}><input type="tel" value={form.parent_phone} onChange={update('parent_phone')} placeholder="(617) 858-9743" /></F>
      <F label={lbl.email}><input type="email" value={form.parent_email} onChange={update('parent_email')} placeholder="name@email.com" /></F>
      <F label={lbl.child}><input value={form.child_name} onChange={update('child_name')} /></F>
      <F label={lbl.age}><input value={form.child_age} onChange={update('child_age')} placeholder="3 years" /></F>
      <F label={lbl.prog}>
        <select value={form.program_interest} onChange={update('program_interest')}>
          <option value="">{lbl.select}...</option>
          {(programs.length ? programs.map(p => (language === 'es' && p.name_es ? p.name_es : p.name)) : PROGRAMS).map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </F>
      <F label={lbl.start}><input type="date" value={form.desired_start_date} onChange={update('desired_start_date')} /></F>
      <F label={lbl.msg}><textarea rows={3} value={form.message} onChange={update('message')} /></F>
      <button type="submit" disabled={submitting} data-testid="home-enroll-submit" style={{ width: '100%', padding: 14, background: primary, color: '#fff', border: 0, borderRadius: 12, cursor: 'pointer', fontSize: 15, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 6 }}>
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        {submitting ? '...' : lbl.submit}
      </button>
      <style>{`
        form input, form select, form textarea {
          width: 100%; box-sizing: border-box; padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 14px; background: #fff; color: #0f172a;
        }
        form label { display:block; font-size:12px; color:#475569; font-weight:600; margin-bottom:6px; }
      `}</style>
    </form>
  );
}

function TourForm({ tenant_slug, primary, language }) {
  const [form, setForm] = useState({ parent_name: '', parent_email: '', parent_phone: '', desired_date: '', desired_time: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const update = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const tr = {
    name: language === 'es' ? 'Su nombre *' : 'Your Name *',
    email: language === 'es' ? 'Correo' : 'Email',
    phone: language === 'es' ? 'Teléfono' : 'Phone',
    date: language === 'es' ? 'Fecha preferida' : 'Preferred Date',
    time: language === 'es' ? 'Hora' : 'Time',
    notes: language === 'es' ? 'Notas' : 'Notes',
    submit: language === 'es' ? 'Reservar Tour' : 'Book Tour',
    thanks: language === 'es' ? '¡Tour solicitado! Te confirmaremos pronto.' : "Tour requested! We'll confirm shortly.",
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.parent_name) return toast.error(language === 'es' ? 'Nombre es requerido' : 'Name is required');
    if (!form.parent_email && !form.parent_phone) return toast.error(language === 'es' ? 'Email o teléfono requerido' : 'Email or phone required');
    setSubmitting(true);
    try {
      // Tour requests are stored as leads with status=tour_scheduled
      await axios.post(`${API}/public/daycare-lead`, {
        tenant_slug,
        source: 'website',
        parent_name: form.parent_name,
        parent_email: form.parent_email,
        parent_phone: form.parent_phone,
        desired_start_date: form.desired_date,
        message: `[TOUR REQUEST] ${form.desired_date || ''} ${form.desired_time || ''}\n${form.message}`,
        status: 'tour_scheduled',
      });
      setSubmitted(true);
    } catch (err) {
      toast.error('Failed to book tour');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div style={{ ...card, marginTop: 32, display: 'inline-block', padding: '32px 28px' }} data-testid="tour-success">
        <CheckCircle2 size={32} color="#16a34a" style={{ marginBottom: 12 }} />
        <p style={{ color: '#0f172a', fontWeight: 600, margin: 0 }}>{tr.thanks}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="tour-form" style={{ background: '#fff', borderRadius: 16, padding: 28, boxShadow: '0 8px 24px rgba(15,23,42,.08)', maxWidth: 520, margin: '32px auto 0', textAlign: 'left' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <F label={tr.name}><input required value={form.parent_name} onChange={update('parent_name')} /></F>
        <F label={tr.phone}><input type="tel" value={form.parent_phone} onChange={update('parent_phone')} /></F>
        <F label={tr.email}><input type="email" value={form.parent_email} onChange={update('parent_email')} /></F>
        <F label={tr.date}><input type="date" value={form.desired_date} onChange={update('desired_date')} /></F>
        <F label={tr.time}><input type="time" value={form.desired_time} onChange={update('desired_time')} /></F>
      </div>
      <F label={tr.notes}><textarea rows={2} value={form.message} onChange={update('message')} /></F>
      <button type="submit" disabled={submitting} data-testid="tour-submit" style={{ width: '100%', padding: 14, background: primary, color: '#fff', border: 0, borderRadius: 12, cursor: 'pointer', fontSize: 15, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <CalIcon size={16} />}
        {tr.submit}
      </button>
      <style>{`
        form input, form select, form textarea { width: 100%; box-sizing: border-box; padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 14px; background: #fff; color: #0f172a; }
        form label { display:block; font-size:12px; color:#475569; font-weight:600; margin-bottom:6px; }
      `}</style>
    </form>
  );
}

function F({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label>{label}</label>
      {children}
    </div>
  );
}

// ============ Styles ============
const fullPage = { minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#f8fafc' };
const navStyle = { background: '#fff', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 8px rgba(15,23,42,.04)' };
const navInner = { maxWidth: 1180, margin: '0 auto', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const brandWrap = { display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' };
const navLinks = { display: 'flex', gap: 24 };
const navA = { color: '#475569', textDecoration: 'none', fontSize: 14, fontWeight: 500 };
const langToggle = { display: 'inline-flex', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' };
const langBtn = { border: 0, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const primaryBtn = (color) => ({ background: color, color: '#fff', border: 0, padding: '12px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 12px rgba(34,197,94,.25)' });
const secondaryBtn = (color) => ({ background: '#fff', color, border: `1px solid ${color}`, padding: '12px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 });
const card = { background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 4px 14px rgba(15,23,42,.05)', border: '1px solid #f1f5f9' };
const footerH = { fontSize: 14, fontWeight: 700, margin: '0 0 10px', color: '#fff' };
const footerLink = { color: '#94a3b8', textDecoration: 'none', fontSize: 13 };
